import { classNames } from '@dimjs/utils';
import { isUndefinedOrNull, LabelValueItem } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { ReactElement, useRef } from 'react';
import { useEffectCustom } from '../hooks';
import { ListItem } from '../list/list-item';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { Picker, PickerColumnItem, PickerProps } from '../picker';
import './style.less';
import { TModalUpPickerDataItem } from './types';

export interface ModalUpPickerProps {
  className?: string;
  visible?: boolean;
  title: string;
  itemHeight?: number;
  pickerDataList: TModalUpPickerDataItem[];
  onChange?: PickerProps['onChange'];
  onClose?: () => void;
  value?: PickerProps['value'];
  drawerProps?: Omit<ModalDrawerProps, 'visible' | 'className'>;
  header?: ReactElement | string;
  onValueChange?: (selectedData?: TModalUpPickerDataItem) => {};
}

/**
 * 自定义选择器
 * ```
 * 1. 可自定义header结构
 * 2. 与Form结合使用赋值与取值方式
 *    例如：
 *    a. 赋值格式: { label: '白金会员', value: '1' }
 *    b. 取值格式:
 *        {
 *          label: '白金会员',
 *          value: '1'
 *        }
 *       可通过使用form.getFormModalPickerValue(value) 直接取值 => '1'
 * ```
 */
export const ModalUpPicker = (props: ModalUpPickerProps) => {
  const selectedTarget = useRef<PickerColumnItem>();

  useEffectCustom(() => {
    if (!isUndefinedOrNull(props.value) && props.pickerDataList.length) {
      const target = props.pickerDataList.find((item) => item.value === props.value);
      if (target) {
        selectedTarget.current = target;
        props.onValueChange?.(target as LabelValueItem);
      }
    } else {
      props.onValueChange?.(undefined);
    }
  }, [props.value, props.pickerDataList]);

  const onChange = hooks.useCallbackRef((pickerColumnItem: PickerColumnItem) => {
    selectedTarget.current = pickerColumnItem;
    props.onValueChange?.(pickerColumnItem);
  });

  const onConfirm = hooks.useCallbackRef(() => {
    props.onChange?.(selectedTarget.current || props.pickerDataList?.[0]);
  });

  return (
    <ModalDrawer
      closeByMask={true}
      onClose={props.onClose}
      {...props.drawerProps}
      className={classNames('v-modal-up-picker', props.className)}
      visible={props.visible || false}
    >
      <div className="vmp-content">
        {props.header ? (
          props.header
        ) : (
          <ListItem
            prefix={
              <span onClick={props.onClose} className="vmp-content-cancel">
                取消
              </span>
            }
            extra={
              <span onClick={onConfirm} className="vmp-content-confirm">
                确定
              </span>
            }
          >
            {props.title}
          </ListItem>
        )}
        <div className="vmp-list">
          <Picker
            itemHeight={props.itemHeight}
            pickerDataList={props.pickerDataList}
            onChange={onChange}
            value={props.value}
          />
        </div>
      </div>
    </ModalDrawer>
  );
};
